/**
 * Header component module.
 * @module shared/components/Header
 */
import React, {useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import styled, {withTheme} from 'styled-components/macro';
import {useSWRConfig} from 'swr';
import history from '../../history';
import IconDropdown from './IconDropdown';
import Popover from './Popover';
import {Badge, Button, Column, DescriptiveText, Icon, Row} from '../../shared';
import {fill, fillHeight} from './cssUtils';
import {goHome} from '../utils/utils';
import {_setNotification} from '../reducers/headerNotification';
import {_openModal} from '../reducers/modals';
import {getHexWithOpacity, hexToRgb} from '../utils/colors';
import Image from './Image';
import {_clearAgent, isEntitled} from '../reducers/user';
import {_clearDefaultContainerID} from '../../advisor/reducers/defaultContainerID';
import lazy from 'lazy.js';

// ToDo: delete this and import from cssUtils when breakpoints are finalized.
const mediaQueries = (prefix, key, sectionLength = 5, extraLength = 0) => style => {
  const breakpoints = {
    sm: 0,
    md: 841,
  };
  if (sectionLength > 5) {
    breakpoints.md += ((sectionLength - 5) * 110);
  }
  if (extraLength) {
    breakpoints.md += extraLength;
  }
  return `@media (${prefix}-width: ${breakpoints[key]}px) {${style}}`;
};
const backgroundImage = props => props.theme.dark
  ? 'rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.05)'
  : 'rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05)';

const _MainHeader = styled.div`
  height: 60px;
  width: auto;
  position: -webkit-sticky; /* Safari */
  position: sticky;
  top: 0;
  background-color: ${props => {
    const rgb = hexToRgb(props.theme.headerBackgroundColor);
    return `rgba(${rgb.r},${rgb.g},${rgb.b}, ${props.theme.headerBackgroundOpacity})`;
  }};
  backdrop-filter: blur(3px);
  border-radius: 0 0 10px 10px;
  padding: 0 5px 0 5px;
  filter: ${props => props.section ? 'none' : 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.5))'};
  z-index: 9;

  ${props => mediaQueries('min', 'md', props.sectionLength)(`
    border-radius: ${props.section ? '0px 0px 0px 0px' : '0px 0px 10px 10px'};
  `)}
`;

const _LargeRow = styled.div`
  ${fillHeight(null)}
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${mediaQueries('min', 'sm')` {
    display: none;
  }`}
  ${props => mediaQueries('min', 'md', props.sectionLength)(`
    display: -webkit-flex; /* Safari */
    display: flex;
  `)}
`;

const _SmallRow = styled.div`
  ${fillHeight(null)}
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${props => mediaQueries('min', 'md', props.sectionLength)(`
    display: none;
  `)}
`;

const _AssistingAdvisor = styled.div`
  ${props => mediaQueries('max', 'md', null, props.extraLength)(`
    display: none;
  `)}
`;

const _SideRow = styled.div`
  display: flex;
`;

const _Relative = styled.div`
  position: relative;
  cursor: pointer;

  &:hover svg #Background {
    opacity: 0.15;
  }
`;

const _ButtonSpacer = styled.div`
  width: 30px;
`;

const getSectionColors = (props) => {
  const backgroundColor = `rgba(${props.theme.dark ? '255, 255, 255, 0.05' : '0, 0, 0, 0.05'})`;

  return `
    background-color: ${props.isSelected ? backgroundColor : 'rgba(0, 0, 0, 0)'};
    &:hover {
      background-color: ${backgroundColor};
      border-radius: 0px !important;
    }
  `;
};

const getSectionIndicatorColors = (props) => {
  if (props.theme.dark) {
    return `
      background-color: ${props.isNav ? props.theme.primaryColor : 'rgba(255, 255, 255, 0.1)'};
    `;
  }
  return `
    background-color: ${props.isNav ? props.theme.primaryColor : 'rgba(0, 0, 0, 0.1)'};
  `;
};

const _Section = styled(Column)`
  position: relative;
  overflow: hidden;
  ${props => getSectionColors(props)}
  cursor: pointer;
`;

const _SectionTitle = styled.div`
  font-family: ${props => props.theme.bodyText.name};
  color: ${props => props.theme.dark ? '#FFFFFF' : '#000000'};
  margin: 0 5px;
`;

const _SectionIndicator = styled.div`
  height: 5px;
  ${fill(null)}
  ${props => getSectionIndicatorColors(props)}
`;

const _Icon = styled.div`
  position: relative;
  cursor: pointer;
  z-index: 0;
`;

const _AttentionIconWrap = styled.div`
  position: absolute;
  top: 2px;
  right: 0;
`;

const _LargeSubHeader = styled.div`
  position: fixed;
  top: 60px;
  ${fill(null)}
  z-index: 10;

  ${mediaQueries('min', 'sm')` {
    display: none;
  }`}
  ${props => mediaQueries('min', 'md', props.sectionLength)(`
    display: -webkit-flex; /* Safari */
    display: flex;
  `)}

  padding: 10px;
  background-color: ${props => {
    const rgb = hexToRgb(props.theme.headerBackgroundColor);
    return `rgba(${rgb.r},${rgb.g},${rgb.b},0.9)`;
  }};
  background-image: linear-gradient(${backgroundImage});
  backdrop-filter: blur(3px);
  border-radius: 0 0 10px 10px;
  filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.5));
`;

const _HeaderNotification = styled.div`
  ${fill(null)};
  position: sticky;
  margin: 3px;
  top: 60px;
  border-left: ${props => props.theme.primaryColor} 3px solid;
  background-color: ${props => getHexWithOpacity(props.theme.primaryColor, 0.5)};
  border-radius: 6px;
  filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.5));
  backdrop-filter: blur(20px);
  z-index: 9;
`;

const trimPathName = (str) => {
  const arr = str.split('/');
  return arr[0] ? arr[0] : arr[1];
};

// TODO Left border color removed from top -> down, turn to gray #797979 or #979797
// TODO Fix sticky top margin to ensure it's always 3px between header and notification when scrolling

/**
 * Header component to serve as the top navigation object.
 * @name Header
 */
const Header = ({
  logo,
  sections,
  icons,
  iconMenuOptions,
  setNotification,
  headerNotification,
  session,
  openModal,
  clearAgent,
  clearDefaultContainerID,
  menuRoutes,
  lastResize,
  platformIcon,
}) => {
  const {mutate} = useSWRConfig();
  const wrapperRef = useRef(null);
  const timerRef = useRef(null);
  const [section, setSection] = useState(null);

  const handleClickOutside = event => {
    if (wrapperRef && wrapperRef.current?.contains && !wrapperRef.current.contains(event.target)) {
      setSection(null);
    }
  };

  useEffect(() => () => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });
  useEffect(() => {
    // if (props.headerNotification && props.headerNotification.message !== this.storedMessage) {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => setNotification(null), 30000);
    // this.storedMessage = props.headerNotification.message;
    // }
  }, [headerNotification]);

  const path = trimPathName(history.location.pathname);
  const filteredSections = sections.map(sec => {
    sec.subsections = sec.subsections && sec.subsections.filter(i => !!i);
    return sec;
  })
    .filter(s => !!(s.subsections && s.subsections.length));
  const logoAction = session?.profile?.role ? () => goHome(session.profile.role, true) : undefined;
  const attentionIcon = icons.find(o => o.image === 'attention');

  return <div>
    <_MainHeader section={section} sectionLength={filteredSections.length}>
      <_LargeRow sectionLength={filteredSections.length}>
        <Row height={'fill'}
             width={'fit-content'}
             wrap={false}
             justifySubRow={'center'}
             alignSubRow={'middle'}
             showDividers={false}>
          <Image src={platformIcon}
                 maxWidth={'40px'}
                 maxHeight={'40px'}
                 margin={'5px'}
                 action={logoAction}/>
          <Image src={logo}
                 maxWidth={'90px'}
                 maxHeight={'40px'}
                 margin={'5px'}
                 action={logoAction}/>
          {filteredSections.map((s) => <_Section key={s.label}
                                                 height={'fill'}
                                                 width={'100px'}
                                                 margin={'0px 5px'}
                                                 alignItems={'center'}
                                                 justifySubColumn={'space between'}
                                                 action={() => {
                                                   if (s.subsections.length === 1) {
                                                     if (s.subsections[0].page) {
                                                       if (window.location.pathname.startsWith('/analyze/dashboard') && s.subsections[0].label === 'Dashboard') {
                                                         mutate('defaultContainer', {force: true}, false);
                                                       }
                                                       history.push(s.subsections[0].page);
                                                     } else if (s.subsections[0].action) {
                                                       s.subsections[0].action(() => setSection(s));
                                                     }
                                                     if (s.subsections[0].sideEffect) {
                                                       s.subsections[0].sideEffect();
                                                     }
                                                   } else if (s.action) {
                                                     s.action(() => setSection(s));
                                                   } else {
                                                     setSection(s);
                                                   }

                                                   if (s.sideEffect) {
                                                     s.sideEffect();
                                                   }
                                                 }}
                                                 isSelected={section?.label === s.label}>
            <div/>
            <_SectionTitle>{s.label}</_SectionTitle>
            <_SectionIndicator isNav={ path === (s.subsections?.[0]?.indicatorPath?.toLowerCase() || s.label.toLowerCase())}>&nbsp;</_SectionIndicator>
            {(s.label === 'Report')
              ? <Badge
                diameter='10px'
                types={['report']}
                position={{top: '10px', right: '2px'}}
                lastResize={lastResize}
                IsSubsectionBadge={false}/>
              : null}
            {(s.label === 'Compliance' && isEntitled([
              {
                group: 'advisor',
                feature: 'client_engagement',
                access: 'r',
              },
            ]))
              ? <Badge
                diameter='10px'
                types={['compliance']}
                position={{top: '10px', right: '2px'}}
                lastResize={lastResize}
                IsSubsectionBadge={false}/>
              : null}
          </_Section>)}
        </Row>
        <Row height={'fill'}
             width={'fit-content'}
             wrap={false}
             justifySubRow={'center'}
             alignSubRow={'middle'}
             showDividers={false}>
          {session.agent && session.agent.role === 'ASSOC' && session.profile.name
              && <_AssistingAdvisor extraLength={160}>
                <DescriptiveText alignment={'right'}
                                 fontSize={'14px'}
                                 font={'ProximaRegular'}
                                 opacity={0.90}
                                 width={'fit-content'}
                                 maxWidth={'140px'}
                                 margin={'10px'}>
                  <Row action={() => {
                    clearAgent();
                    clearDefaultContainerID();
                    return history.push('/assist');
                  }}>
                    Assisting: {session.profile.name}
                  </Row>
                </DescriptiveText>
              </_AssistingAdvisor>}
          {icons.map((icon) => (
            <_Relative
              key={icon.image}
              onClick={() => {
                if (typeof (icon.action) === 'function') {
                  icon.action();
                }
                if (typeof (icon.action) === 'string') {
                  history.push(icon.action);
                }
              }}
            >
              {(icon.image === 'notifications')
                ? <Badge
                  diameter='10px'
                  types={['notification']}
                  position={{top: '10px', right: '2px'}}
                  lastResize={lastResize}
                  IsSubsectionBadge={false}/>
                : null}
              <Popover trigger='hover'
                       placement='bottom'
                       disabled={!icon.popover}
                       content={icon.popover?.text}>
                <_Icon>
                  <Icon key={icon.image}
                        color={icon.color}
                        height={icon.height || '40px'}
                        width={icon.width || '40px'}
                        margin={icon.margin}
                        svg={icon.image}
                  />
                </_Icon>
              </Popover>
            </_Relative>))}
          <IconDropdown icon='gear'
                        options={iconMenuOptions}
                        position={{right: -5, top: 8}}
                        currentSelection={lazy(iconMenuOptions)
                          .pluck('options')
                          .flatten()
                          .find(o => o.value === window.location.pathname)}
                        isDisplayNotificationsBadge={true}/>
        </Row>
      </_LargeRow>
      <_SmallRow sectionLength={filteredSections.length}>
        {!!filteredSections.length && <_Relative
          onClick={() => openModal({
            type: 'MenuModal',
            sections: filteredSections,
            logo: logo,
            role: session && session.profile && session.profile.role,
          })}
        >
          <Badge
            diameter='10px'
            types={['report', 'compliance']}
            position={{top: '2px', right: '2px'}}
            lastResize={lastResize}
            IsSubsectionBadge={false}/>
          <_Icon>
            <Icon svg={'mainMenu'} width={'40px'} height={'40px'}/>
          </_Icon>
        </_Relative>}
        <Image src={session && session.profile && session.profile.brand && session.profile.brand.advisorIcon}
               maxWidth={'40px'}
               maxHeight={'40px'}
               margin={'5px'}
               action={logoAction}/>
        <_SideRow>
          {attentionIcon && (
            <_Relative key={attentionIcon.image}>
              <_AttentionIconWrap onClick={() => {
                if (typeof (attentionIcon.action) === 'function') {
                  attentionIcon.action();
                }
                if (typeof (attentionIcon.action) === 'string') {
                  history.push(attentionIcon.action);
                }
              }}>
                <Popover trigger='hover'
                         placement='bottom'
                         disabled={!attentionIcon.popover}
                         content={attentionIcon.popover?.text}>
                  <_Icon>
                    <Icon color={attentionIcon.color}
                          height={attentionIcon.height}
                          width={attentionIcon.width}
                          margin={attentionIcon.margin}
                          svg={attentionIcon.image}
                    />
                  </_Icon>
                </Popover>
              </_AttentionIconWrap>
            </_Relative>
          )}
          <IconDropdown icon='gear'
                        options={iconMenuOptions}
                        position={{right: -5, top: 8}}
                        lastResize={lastResize}
                        currentSelection={lazy(iconMenuOptions)
                          .pluck('options')
                          .flatten()
                          .find(o => o.value === window.location.pathname)}
                        isDisplayNotificationsBadge={true}/>
        </_SideRow>
      </_SmallRow>
    </_MainHeader>
    {section && section.subsections && <_LargeSubHeader ref={wrapperRef} sectionLength={filteredSections.length}>
      <Row width={'fill'}
           wrap={true}
           justifySubRow={'left'}
           alignSubRow={'top'}
           alignItems={'middle'}
           showDividers={false}>
        {section.subsections
          .map((subsection, index) => <React.Fragment key={index}>
            <Button importance={'secondary'}
                    size={'md'}
                    label={subsection.label}
                    disabled={false}
                    width={'150'}
                    action={() => {
                      if (subsection.page) {
                        if (window.location.pathname.startsWith('/analyze/dashboard') && subsection.label === 'Dashboard') {
                          mutate('defaultContainer', {force: true}, false);
                        }
                        history.push(subsection.page);
                        setSection(null);
                      } else if (subsection.action) {
                        subsection.action(() => setSection(null));
                      }

                      if (subsection.sideEffect) {
                        subsection.sideEffect();
                      }
                    }}
            />
            {index !== section.subsections.length - 1 ? <_ButtonSpacer/> : null}
          </React.Fragment>)}
      </Row>
    </_LargeSubHeader>}
    {headerNotification
    && <_HeaderNotification onClick={timerRef.current && (() => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setNotification(null), 30000);
    })}>
      <Row justifySubRow={'space between'}>
        <Row justifySubRow={'left'}
             width={'150px'} layout={{grow: 1}}>
          <DescriptiveText preset={'standard'}
                           alignment={'left'}>{headerNotification.message}</DescriptiveText>
          {headerNotification.buttons?.length
          && <Row justifySubRow={'left'} width={'fit-content'}>
            {headerNotification.backAction
            && <Button importance={'secondary'}
                       size={'sm'}
                       label={'Back'}
                       action={() => {
                         setNotification(null);
                         return headerNotification.backAction();
                       }}/>}
            {headerNotification.buttons?.map((button, index) => <Button
              key={'ha-' + index.toString()}
              importance={'secondary'}
              size={'sm'}
              label={button.label}
              action={() => {
                setNotification(null);
                return button.action();
              }}/>)}
          </Row>}
        </Row>
        <Icon svg={'close'}
              margin={'5px 10px 5px 10px'}
              rotate={'90deg'}
              width={'20px'}
              height={'20px'}
              action={() => setNotification(null)}/>
      </Row>
    </_HeaderNotification>}
    {filteredSections.map(s => <div key={s.label}>{s.content}</div>)}
    {menuRoutes}
  </div>;
};

const mapStateToProps = state => ({
  session: state.session,
  headerNotification: state.headerNotification,
});

const mapDispatchToProps = dispatch => ({
  setNotification: notification => dispatch(_setNotification(notification)),
  openModal: modal => dispatch(_openModal(modal)),
  clearAgent: () => dispatch(_clearAgent()),
  clearDefaultContainerID: () => {
    dispatch(_clearDefaultContainerID());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(withTheme(Header));

Header.propTypes = {
  /**
   * Image Url
   */
  session: PropTypes.shape().isRequired,
  logo: PropTypes.string,
  platformIcon: PropTypes.string,
  /**
   * Array of section objects. Each section has a label to display, page/route to direct to when clicked, and content to display. Content should be JSX.
   */
  sections: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    page: PropTypes.string,
    action: PropTypes.func,
    content: PropTypes.node,
    sideEffect: PropTypes.func,
    lastResize: PropTypes.object,
  })),
  /**
   * Array of image objects. Each image has an image url, and a function to execute or page/route to direct to when clicked.
   */
  icons: PropTypes.arrayOf(PropTypes.shape({
    image: PropTypes.string,
    action: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.func,
    ]),
  })),
  /**
   * IconDropdown options object.
   */
  iconMenuOptions: PropTypes.shape({
    mainSection: PropTypes.shape({
      label: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        action: PropTypes.func,
        value: PropTypes.string,
      })).isRequired,
    }).isRequired,
    secondarySection: PropTypes.shape({
      label: PropTypes.string,
      options: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        action: PropTypes.func,
        value: PropTypes.string,
      })).isRequired,
    }),
  }),
  headerNotification: PropTypes.shape({
    message: PropTypes.string.isRequired,
    backAction: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
    buttons: PropTypes.arrayOf(PropTypes.shape({
      action: PropTypes.func.isRequired,
      label: PropTypes.string.isRequired,
    })),
  }),
  setNotification: PropTypes.func.isRequired,
  menuRoutes: PropTypes.node,
  /**
   * The open function that dispatches _openModal from Redux
   */
  openModal: PropTypes.func.isRequired,
  clearAgent: PropTypes.func.isRequired,
  clearDefaultContainerID: PropTypes.func.isRequired,
  /**
   * Last window resize (moment)
   */
  lastResize: PropTypes.object.isRequired,
};
